import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import {
  changeMode,
  changeModeForField,
  convertFileToBase64Model,
  convertBase64ToFile,
  fieldTruePermission,
  prepareDefaultValues,
  prepareInitialConfig
} from "../../../../utils/formHelper";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { ADD_CLIENT, GET_CLIENT, UPDATE_CLIENT, POST_GET_COMPANY_NAME, GET_COMPANY_DETAILS } from "../../../../utils/services/apiPath";
import { getApi, postApi, putApi } from "../../../../utils/services";
import { encryptText } from "../../../../utils/crypto";
import {MobileField, EmailField, FileUploadField, ZipField, UrlField, ProButton, EinField, ProTextInput, AutoCompleteWithInput, FormSectionWrapper, ProFormsLayout} from "@proscore-ai/proscore-ui-library";

const ClientProfileFormConfing = {
  companyName: {
    label: "Company Name",
    rules: { required: "Company Name is required!" }
  },
  title: {
    label: "Title"
  },
  contactFirstName: {
    label: "Contact First Name",
    rules: { required: "Contact First Name is required!" }
  },
  contactLastName: {
    label: "Contact Last Name",
    rules: { required: "Contact Last Name is required!" }
  },
  address1: {
    label: "Address 1",
    rules: { required: "Address 1 is required!" }
  },
  address2: {
    label: "Address 2"
  },
  city: {
    label: "City",
    rules: { required: "City is required!" }
  },
  state: {
    label: "State",
    rules: { required: "State is required!" }
  },
  zip: {
    label: "Zip",
    rules: { required: "Zip is required!" }
  },
  website: {
    label: "Website"
  },
  contactPhone: {
    label: "Contact Phone",
    rules: {
      required: "Contact Phone is required!",
      rules: {
        pattern: {
          value: /^.{14}$/,
          message: "Whats App Number must be 10 digits"
        }
      }
    }
  },
  contactEmail: {
    label: "Contact Email",
    rules: { required: "Contact Email is required!" }
  },
  fax: {
    label: "Fax",
  },
  invitationCode: {
    label: "Invitation Code"
  },
  logoMediaModel: {
    label: "Logo",
    defaultValue: null
  },
  ein: {
    label: "EIN",
    rules: { 
      required: "EIN is required!",
      validate: {
        format: value => {
          const stripped = value?.replace(/-/g, "") ?? "";
          return /^\d{9}$/.test(stripped) || "EIN must follow the format XX-XXXXXXX.";
        },
        noAllZeroes: value => {
          const stripped = value?.replace(/-/g, "") ?? "";
          return stripped === "000000000" ? "EIN cannot contain all zeros." : true;
        }
      }
    }
  },
  aiImportAlias: {
    label: "AI Import Alias",
    defaultValue: null
  },
};

export default function ClientProfile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAddMode = useMemo(() => id === "add", [id]);
  const [clientProfileData, setClientProfileData] = useState({});
  const [status, setStatus] = useState(STAUTES.IDLE);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(ClientProfileFormConfing, isAddMode ? "edit" : "read"));
  const { handleSubmit, control, setValue } = useForm({ defaultValues: {companyName: "", ...prepareDefaultValues(ClientProfileFormConfing)} });
  const [isSaving, setIsSaving] = useState(false);
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);
  const backAction = () => navigate("/clients");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);

  const onEdit = () => setFormConfig(changeMode("edit"));
  const onCancel = () => (isAddMode ? backAction() : setFormConfig(changeMode()));

  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  function generatePassword(length) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      password += characters[randomIndex];
    }
    if (!/[!@#$%^&*()\-_=+]/.test(password)) {
      const specialCharacters = "!@#$%^&*()-_=+";
      const randomSpecialChar = specialCharacters[Math.floor(Math.random() * specialCharacters.length)];
      const randomIndex = Math.floor(Math.random() * (password.length + 1));
      password = password.slice(0, randomIndex) + randomSpecialChar + password.slice(randomIndex);
    }

    return password;
  }

  useEffect(() => {
    const timeout = setTimeout(async () => {
      const trimmedSearch = searchTerm.trim();
  
      if (trimmedSearch.length >= 3) {
        setIsCompanyLoading(true);
        try {
          const payload = {
            searchString: trimmedSearch,
            pageSize: 10,
            pageIndex: 0,
            orderByAscending: true,
            orderCol: "partnerName",
          };
          const response = await postApi(POST_GET_COMPANY_NAME, payload);
          setCompanyOptions(response?.data || []);
        } catch (err) {
          errorToast("Failed to fetch company list");
          setCompanyOptions([]);
        } finally {
          setIsCompanyLoading(false);
        }
      } else {
        setCompanyOptions([]);
        setIsCompanyLoading(false);
      }
    }, 500);
  
    return () => clearTimeout(timeout);
  }, [searchTerm]);
  
  const onSubmit = async data => {
    if (isSaving) return; 
    setIsSaving(true);
    setStatus(STAUTES.LOADING);
    let base64ProfilePicture = null;
    if (data.logoMediaModel instanceof Blob) {
      base64ProfilePicture = await convertFileToBase64Model(data.logoMediaModel);
    }
    const password = generatePassword(8);
    const payload = {
      ...clientProfileData,
      ...data,
      ein: data.ein.replace(/-/g, ""),
      password: password,
      encryptedPassword: encryptText(password),
      proscoreAdminPassword: encryptText(`@d1n4${data.companyName.replace(/\s+/g, "")}`),
      logoMediaModel: base64ProfilePicture || null,
      logo: base64ProfilePicture ? data.logoMediaModel.name : clientProfileData.logo || "", 
    };
    delete payload.logo;
    const method = isAddMode ? postApi : putApi;
    const { error } = await method(isAddMode ? ADD_CLIENT : UPDATE_CLIENT, payload);
    setIsSaving(false); 
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    successToast(`Client ${isAddMode ? "Added" : "Updated"} Successfully`);
    backAction();
  };

  const getClientProfileData = async id => {
    const res = await getApi(`${GET_CLIENT}${id}`);
    const data = {
      ...res,
      logoMediaModel: res?.logoMediaModel || null
    };
    if (res?.logoMediaModel?.base64String) {
      const file = convertBase64ToFile(
        res.logoMediaModel.base64String,
        res.logoMediaModel.fileName || "logo.jpg"
      );
      file.preview = URL.createObjectURL(file);
      data.logoMediaModel = file;
    }
    if (!res) return errorToast(error);
    setClientProfileData(data);
    Object.keys(formConfig).forEach(field => setValue(field, field === "logoMediaModel" ? data.logoMediaModel : data[field] ?? ""));
  };

  useEffect(() => {
    if (!isAddMode) getClientProfileData(id);
  }, [id]);

  const defaultFormProps = { control, formValues: clientProfileData, isAddMode, handleReadValueClick };

  const handleGenerate = () => {
    setFormConfig(changeModeForField("invitationCode", "edit"));
    const result = Math.floor(100000 + Math.random() * 900000);
    setValue("invitationCode", result.toString());
  };

  const handleCompanySelect = async (event, selected) => {
    if (!selected || (typeof selected === "string" && selected.trim() === "")) {
      const clearedFields = Object.keys(ClientProfileFormConfing).filter(
        field => !["companyName", "logoMediaModel"].includes(field)
      );
      clearedFields.forEach(field => setValue(field, ""));
      setValue("logoMediaModel", null);
      return;
    }
  
    if (typeof selected === "string") {
      setValue("companyName", selected);
      return;
    }
  
    const clientId = selected?.clientId;
    if (!clientId) return;
  
    try {
      const data = await getApi(`${GET_COMPANY_DETAILS}/${clientId}`);
      if (!data) return;
  
      const { logoMediaModel, logo, contactPhone, contactNumber, ...rest } = data;
  
      const file = logoMediaModel?.base64String
        ? (() => {
            const fileName = logo?.split("/")?.pop() || "logo.jpg";
            const file = convertBase64ToFile(logoMediaModel.base64String, fileName);
            file.preview = URL.createObjectURL(file);
            return file;
          })()
        : null;
  
      const normalizedData = {
        ...rest,
        contactPhone: contactPhone || contactNumber || "",
        logoMediaModel: file,
        ein: data.taxId || ""
      };
  
      Object.keys(ClientProfileFormConfing).forEach(field => {
        setValue(field, normalizedData[field] ?? "");
      });
    } catch (err) {
      errorToast("Unable to fetch company details. Please try again.");
    }
  };
  
  return (
    <ProFormsLayout
      status={status}
      backAction={backAction}
      title={`${isAddMode ? "Add" : "Edit"} Client`}
      backLabel="Go Back to All Clients"
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      onCancel={onCancel}
      onSave={handleSubmit(onSubmit)}
      isSaving={isSaving}
    >
      <FormSectionWrapper title={"CLIENT INFORMATION"}>
        <AutoCompleteWithInput {...formConfig.companyName} {...defaultFormProps} permission={fieldTruePermission} options={companyOptions} labelKey="partnerName" fetchOptions={setSearchTerm} handleSelect={handleCompanySelect} loading={isCompanyLoading}/>
        <ProTextInput {...defaultFormProps} {...formConfig.title} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.contactFirstName} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.contactLastName} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.address1} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.address2} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.city} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.state} permission={fieldTruePermission} />
        <ZipField {...defaultFormProps} {...formConfig.zip} permission={fieldTruePermission} />
        <UrlField {...defaultFormProps} {...formConfig.website} permission={fieldTruePermission} />
        <MobileField {...defaultFormProps} {...formConfig.contactPhone} permission={fieldTruePermission} />
        <EmailField {...defaultFormProps} {...formConfig.contactEmail} permission={fieldTruePermission} />
        <MobileField {...defaultFormProps} {...formConfig.fax} permission={fieldTruePermission} />
        {!isAddMode && (
          <>
            <ProTextInput {...defaultFormProps} {...formConfig.invitationCode} disabled={true} permission={fieldTruePermission} />
            <div style={{marginLeft:"15px",marginTop:"15px"}}> <ProButton onClick={handleGenerate}>Re-Generate</ProButton></div>
          </>
        )}
        <FileUploadField
          {...defaultFormProps}
          {...formConfig.logoMediaModel}
          inputProps={{ accept: "image/*" }}
          permission={fieldTruePermission} showPreview={true}
        />
        <EinField {...defaultFormProps} {...formConfig.ein} permission={fieldTruePermission} />
        <ProTextInput {...defaultFormProps} {...formConfig.aiImportAlias} permission={fieldTruePermission} />
      </FormSectionWrapper>
    </ProFormsLayout>
  );
}